/* eslint-disable @typescript-eslint/no-explicit-any */
import { compressToUTF16 } from './compression'
import { CompressionWorkerRequest, CompressionWorkerResponse } from './metadata-compression.types'

const MODELS_METADATA_KEY = 'MODELS_METADATA'

export type ModelsMetadataCompressionWorkerFactory = () => Worker

let compressionWorker: Worker | null = null
let compressionRequestId = 0
let workerUnavailable = false
let compressionWorkerFactory: ModelsMetadataCompressionWorkerFactory | null = null
let modelsMetadataWriteQueue = Promise.resolve()
const compressionRequests = new Map<number, { resolve: (compressed: string) => void; reject: (error: Error) => void }>()

function resetCompressionWorker() {
  compressionWorker?.terminate()
  compressionWorker = null
}

export function setModelsMetadataCompressionWorkerFactory(
  factory: ModelsMetadataCompressionWorkerFactory | null,
): void {
  compressionWorkerFactory = factory
  workerUnavailable = false
  resetCompressionWorker()

  if (compressionRequests.size > 0) {
    const rejectionError = new Error('Metadata compression worker has been reconfigured')
    compressionRequests.forEach(({ reject }) => reject(rejectionError))
    compressionRequests.clear()
  }
}

function createCompressionWorker(): Worker | null {
  if (workerUnavailable || typeof Worker === 'undefined' || !compressionWorkerFactory) {
    return null
  }

  if (!compressionWorker) {
    try {
      compressionWorker = compressionWorkerFactory()
    } catch {
      workerUnavailable = true
      return null
    }

    compressionWorker.onmessage = (event: MessageEvent<CompressionWorkerResponse>) => {
      const request = compressionRequests.get(event.data.id)
      if (!request) {
        return
      }

      compressionRequests.delete(event.data.id)

      if (event.data.error) {
        request.reject(new Error(event.data.error))
        return
      }

      request.resolve(event.data.compressed ?? '')
    }
    compressionWorker.onerror = () => {
      workerUnavailable = true
      resetCompressionWorker()

      compressionRequests.forEach(({ reject }) => {
        reject(new Error('Failed to compress metadata in a worker'))
      })
      compressionRequests.clear()
    }
  }

  return compressionWorker
}

function compressModelsMetadata(data: unknown): Promise<string> {
  const worker = createCompressionWorker()
  if (!worker) {
    return Promise.resolve(compressToUTF16(JSON.stringify(data)))
  }

  return new Promise<string>((resolve, reject) => {
    const id = ++compressionRequestId
    compressionRequests.set(id, { resolve, reject })

    try {
      const request: CompressionWorkerRequest = { id, data }
      worker.postMessage(request)
    } catch (error) {
      compressionRequests.delete(id)
      reject(error instanceof Error ? error : new Error('Failed to post metadata compression job'))
    }
  })
}

export function writeModelsMetadataAsync(
  data: unknown,
  store: Storage,
  memoryCache: Record<string, any>,
): Promise<void> {
  memoryCache[MODELS_METADATA_KEY] = data
  const compressionPromise = compressModelsMetadata(data).catch(() => {
    workerUnavailable = true
    resetCompressionWorker()
    return compressToUTF16(JSON.stringify(data))
  })

  modelsMetadataWriteQueue = modelsMetadataWriteQueue
    .catch(() => undefined)
    .then(async () => {
      const compressedData = await compressionPromise
      store.setItem(MODELS_METADATA_KEY, compressedData)
    })

  return modelsMetadataWriteQueue
}
